/**
 * Canonical site origin for HTML meta (OG, Twitter, canonical), sitemaps, and RSS.
 *
 * Resolution order:
 * 1. SITE_URL or PUBLIC_SITE_URL — set in CI/Vercel for an explicit canonical URL
 * 2. VERCEL_PROJECT_PRODUCTION_URL — production hostname on Vercel (system env)
 * 3. VERCEL_URL — current deployment hostname (preview or production build)
 * 4. Default — https://www.c1.ai
 *
 * @see https://vercel.com/docs/projects/environment-variables/system-environment-variables
 */
export function resolveSiteUrl(): string {
  const explicit =
    process.env.SITE_URL?.trim() || process.env.PUBLIC_SITE_URL?.trim();
  if (explicit) {
    return normalizeOrigin(explicit);
  }

  const productionHost = process.env.VERCEL_PROJECT_PRODUCTION_URL?.trim();
  if (productionHost && process.env.VERCEL_ENV === "production") {
    return hostToHttpsOrigin(productionHost);
  }

  const vercelUrl = process.env.VERCEL_URL?.trim();
  if (vercelUrl) {
    return hostToHttpsOrigin(vercelUrl);
  }

  return "https://www.c1.ai";
}

function normalizeOrigin(url: string): string {
  const trimmed = url.trim();
  const withProto = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const u = new URL(withProto);
    return `${u.protocol}//${u.host}`;
  } catch {
    return trimmed.replace(/\/$/, "");
  }
}

function hostToHttpsOrigin(hostOrUrl: string): string {
  const trimmed = hostOrUrl.trim();
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const u = new URL(trimmed);
      return `${u.protocol}//${u.host}`;
    }
  } catch {
    /* use hostname parsing below */
  }
  const host = trimmed.replace(/\/.*$/, "").replace(/\/$/, "");
  return `https://${host}`;
}
